import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  functionalUpdate,
  PaginationState,
  Updater,
} from '@tanstack/react-table';
import { isEqual } from 'lodash-es';

import { DEFAULT_PAGINATION_PAGE_SIZE } from 'src/common/external/bambi-api/constants';

/**
 *
 * @param params Filters, sorting, etc that when changed should reset the page to the first page
 * @returns pagination state and handlePaginationChange for our DataGrid pagination config
 *
 * We had some issues with pagination triggering extra requests on page changes.
 * The culprit for those turned out to be calling refetch when filters changed, etc.
 *
 * We do have usePagination but that doesn't handle resetting back to the first page when filters change
 * This also lets use have all the params for the query defined before the hook
 * and avoids the need for refetch. It also requires maintaining page state
 * outside of the hook.
 */
export function usePaginationState<TParams extends Record<string, unknown>>(
  params: TParams,
  initial: Partial<PaginationState> = {}
): {
  paramsWithPage: TParams & { page: number };
  pagination: PaginationState;
  handlePaginationChange: (paginationUpdater: Updater<PaginationState>) => void;
} {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGINATION_PAGE_SIZE,
    ...initial,
  });

  const handlePaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      setPagination((old) => functionalUpdate(updater, old));
    },
    []
  );

  const deferredParams = useDeferredValue(params);
  const paramsChanged = useMemo(() => {
    return !isEqual(params, deferredParams);
  }, [params, deferredParams]);

  useEffect(() => {
    if (paramsChanged) {
      setPagination((old) => ({ ...old, pageIndex: 0 }));
    }
  }, [paramsChanged]);
  const paginationToReturn = useMemo(() => {
    return paramsChanged
      ? {
          ...pagination,
          pageIndex: 0,
        }
      : pagination;
  }, [pagination, paramsChanged]);

  const paramsWithPage = useMemo(() => {
    return {
      ...params,
      page: paginationToReturn.pageIndex + 1,
    };
  }, [params, paginationToReturn]);

  return {
    paramsWithPage,
    pagination: paginationToReturn,
    handlePaginationChange,
  };
}
